import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseApi } from '../utils/api';
import { withSarPrice } from '../utils/coursePrice';

interface FormData {
  courseName: string;
  grade: string;
  term: string;
  active: boolean;
  price: string;
  sarPrice: string;
  description: string;
  imageFile: File | null;
  enrollmentDurationDays: string;
}

const AddCourse = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    courseName: '',
    grade: '2', // Fixed to Secondary3 (value 2)
    term: '',
    active: false,
    price: '',
    sarPrice: '',
    description: '',
    imageFile: null,
    enrollmentDurationDays: '0',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Create FormData for multipart/form-data submission
      const formDataToSend = new FormData();
      formDataToSend.append('courseName', withSarPrice(formData.courseName, formData.sarPrice));
      formDataToSend.append('grade', formData.grade);
      formDataToSend.append('term', formData.term);
      formDataToSend.append('active', formData.active.toString());
      formDataToSend.append('price', formData.price);
      
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      formDataToSend.append(
        'enrollmentDurationDays',
        String(Math.max(0, parseInt(formData.enrollmentDurationDays, 10) || 0))
      );
      
      if (formData.imageFile) {
        formDataToSend.append('imageFile', formData.imageFile);
      }

      const response = await courseApi.addCourseWithImage(formDataToSend);
      
      if (response.success) {
        navigate('/courses');
      } else {
        setError(response.message || 'Failed to create course');
      }
    } catch (error) {
      console.error('Error creating course:', error);
      setError('An error occurred while creating the course');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({
      ...prev,
      imageFile: file,
    }));
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-dark">Add New Course</h1>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {error && (
          <div className="mb-4 p-4 text-red-700 bg-red-100 border border-red-300 rounded-md">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Course Name *</label>
            <input
              type="text"
              name="courseName"
              value={formData.courseName}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Term *</label>
            <select
              name="term"
              value={formData.term}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              required
            >
              <option value="">Select Term</option>
              <option value="0">First Term</option>
              <option value="1">Second Term</option>
              <option value="2">Third Term</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">السعر (ريال) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="sarPrice"
              value={formData.sarPrice}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">السعر (جنيه) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">مدة التسجيل بالأيام</label>
            <input
              type="number"
              min="0"
              step="1"
              name="enrollmentDurationDays"
              value={formData.enrollmentDurationDays}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
            />
            <p className="mt-1 text-xs text-gray-500">القيمة 0 تعني أن الوصول لا ينتهي حسب المدة.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              placeholder="Optional course description"
            />
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Course Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
            />
            {formData.imageFile && (
              <p className="mt-1 text-sm text-gray-600">Selected: {formData.imageFile.name}</p>
            )}
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/courses')}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Add Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCourse; 